'use client'

import { useCallback, useEffect, useRef } from 'react'

/** Default lock window (ms) during which repeat taps are ignored. */
export const TAP_LOCK_MS = 500

/**
 * Guard against rapid double-taps on synchronous CTAs (e.g. Add to Cart).
 *
 * The handler completes in ~30ms, so a fast double-tap fires it twice and
 * silently doubles the dispatched quantity. HeroUI v3 Button ignores
 * isLoading, and a state-based flag set and reset in the same handler never
 * renders, so the guard must be a synchronous ref lock instead.
 *
 * Returns an `acquire` function: the first call returns true and locks for
 * `lockMs`; calls made while the lock is active return false and the caller
 * must treat the tap as a no-op. Users who genuinely want two units can tap
 * again after the window or adjust quantity in the cart.
 *
 * Usage:
 *   const acquireTapLock = useTapLock()
 *   const handlePress = () => {
 *     if (!acquireTapLock()) return
 *     dispatch(addToCart(...))
 *   }
 */
export function useTapLock(lockMs: number = TAP_LOCK_MS): () => boolean {
  const lockedRef = useRef(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Clear any pending unlock timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  return useCallback(() => {
    if (lockedRef.current) return false
    lockedRef.current = true
    timerRef.current = setTimeout(() => {
      lockedRef.current = false
      timerRef.current = null
    }, lockMs)
    return true
  }, [lockMs])
}
